/**
 * Application Constants
 */

// Polling intervals (in milliseconds)
export const POLLING_INTERVALS = {
  FAST: 5000,      // 5 seconds - for appointments, contacts
  NORMAL: 15000,   // 15 seconds - for less critical data
  SLOW: 30000,     // 30 seconds - for leasing warnings
  // Specific polling intervals for dashboard hooks
  APPOINTMENTS: 5000,
  CONTACTS: 5000,
  LEASING_WARNINGS: 30000,
  MESSAGES: 3000,  // 3 seconds - for real-time chat
  DEFAULT: 5000,   // Default polling interval
} as const;

// Stale times for React Query
export const STALE_TIMES = {
  FAST: 2000,
  NORMAL: 10000,
  SLOW: 20000,
} as const;

// Action card types
export const ACTION_CARD_TYPES = {
  URGENT: 'urgent',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
} as const;

export type ActionCardType = (typeof ACTION_CARD_TYPES)[keyof typeof ACTION_CARD_TYPES];

// Navigation items
export const NAV_ITEMS = [
  { icon: 'LayoutDashboard', path: '/', label: 'Dashboard' },
  { icon: 'Users', path: '/contacts', label: 'Kontakte' },
  { icon: 'Calendar', path: '/calendar', label: 'Kalender' },
  { icon: 'Settings', path: '/settings', label: 'Einstellungen' },
] as const;

// Appointment type colors for borders
export const APPOINTMENT_TYPE_COLORS = {
  in_person: 'border-l-orange-500',
  phone: 'border-l-blue-500',
  pickup: 'border-l-purple-500',
} as const;

// Appointment category colors (for badges/cards)
export const APPOINTMENT_CATEGORY_COLORS = {
  service: 'bg-blue-500/20 text-blue-400',
  abholung: 'bg-purple-500/20 text-purple-400',
} as const;

// Calendar appointment categories with full styling (from wireframe)
export const CALENDAR_CATEGORIES = {
  service: {
    label: 'Service',
    color: '#3498db',
    gradient: 'linear-gradient(135deg, #3498db, #2980b9)',
    borderColor: '#1f6fa3',
    textColor: '#ffffff',
  },
  abholung: {
    label: 'Abholung',
    color: '#9b59b6',
    gradient: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
    borderColor: '#7d3c98',
    textColor: '#ffffff',
  },
  beratung: {
    label: 'Beratung',
    color: '#2ecc71',
    gradient: 'linear-gradient(135deg, #2ecc71, #27ae60)',
    borderColor: '#1e8449',
    textColor: '#ffffff',
  },
  internal_block: {
    label: 'Intern',
    color: '#64748b',
    gradient: 'linear-gradient(135deg, #64748b, #475569)',
    borderColor: '#334155',
    textColor: '#ffffff',
  },
} as const;

export type CalendarCategory = keyof typeof CALENDAR_CATEGORIES;

// Distinct color palette for custom location services
// Avoids green, blue, orange, red, purple already used by static categories
const SERVICE_COLOR_PALETTE = [
  { color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6, #0d9488)', borderColor: '#0f766e', textColor: '#ffffff' }, // teal
  { color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899, #db2777)', borderColor: '#be185d', textColor: '#ffffff' }, // pink
  { color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)', borderColor: '#4338ca', textColor: '#ffffff' }, // indigo
  { color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', borderColor: '#b45309', textColor: '#1a1a1a' }, // amber
  { color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', borderColor: '#0e7490', textColor: '#ffffff' }, // cyan
  { color: '#f43f5e', gradient: 'linear-gradient(135deg, #f43f5e, #e11d48)', borderColor: '#be123c', textColor: '#ffffff' }, // rose
  { color: '#84cc16', gradient: 'linear-gradient(135deg, #84cc16, #65a30d)', borderColor: '#4d7c0f', textColor: '#1a1a1a' }, // lime
  { color: '#d946ef', gradient: 'linear-gradient(135deg, #d946ef, #c026d3)', borderColor: '#a21caf', textColor: '#ffffff' }, // fuchsia
  { color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)', borderColor: '#0369a1', textColor: '#ffffff' }, // sky
  { color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)', borderColor: '#047857', textColor: '#ffffff' }, // emerald
] as const;

/** Deterministic color for a custom service name (hash-based) */
export function getServiceColor(serviceName: string) {
  let hash = 0;
  for (let i = 0; i < serviceName.length; i++) {
    hash = ((hash << 5) - hash + serviceName.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % SERVICE_COLOR_PALETTE.length;
  return SERVICE_COLOR_PALETTE[idx];
}

// Calendar time configuration
export const CALENDAR_CONFIG = {
  START_HOUR: 0,
  END_HOUR: 24,
  SLOT_HEIGHT: 60, // pixels per hour
  TIME_COLUMN_WIDTH: 60, // pixels
} as const;

// Leasing constants
export const LEASING = {
  DURATION_MONTHS: 36,
  FOLLOWUP_MONTH: 33,
  WARNING_DAYS_BEFORE: 7,
} as const;
